<template>
  <!-- ICONIC: Kanäle & Leute – live sehen, wer wo ist, Leute und Kanäle verwalten -->
  <v-container fluid class="live">
    <div class="d-flex flex-wrap align-end mb-3">
      <div class="mr-auto mb-2">
        <h1 class="text-h5 font-weight-medium">Kanäle & Leute</h1>
        <div class="text-body-2 text--secondary">
          {{ onlineCount }} online · Kanal oder Person antippen
        </div>
      </div>
      <v-btn
        color="primary"
        depressed
        class="mb-2"
        @click="newChannel"
      >
        <v-icon left>mdi-plus</v-icon>Neuer Kanal
      </v-btn>
    </div>

    <v-row>
      <v-col cols="12" md="6" lg="5">
        <v-card outlined>
          <div class="d-flex align-center px-3 pt-3 flex-wrap">
            <v-text-field
              v-model="search"
              prepend-inner-icon="mdi-magnify"
              label="Person suchen"
              clearable
              hide-details
              outlined
              dense
              class="mr-3 mb-3 search"
            ></v-text-field>
            <v-switch
              v-model="showEmpty"
              label="Leere Kanäle zeigen"
              hide-details
              inset
              dense
              class="mt-0 mb-3"
            ></v-switch>
          </div>
          <v-divider></v-divider>
          <v-progress-linear
            v-if="loading && !rows.length"
            indeterminate
          ></v-progress-linear>
          <div class="tree" role="tree" aria-label="Kanäle und Leute">
            <template v-for="row in rows">
              <!-- Trennlinie -->
              <div
                v-if="row.kind === 'channel' && row.channel && row.spacer"
                :key="row.key"
                class="spacer-row"
                :class="`text-${row.spacer.align === 'c' ? 'center' : row.spacer.align === 'r' ? 'right' : 'left'}`"
                :style="indent(row)"
                @click="selectChannel(row.channel)"
              >
                <v-divider v-if="row.spacer.line"></v-divider>
                <span v-else class="text-caption text--secondary">{{
                  row.spacer.text
                }}</span>
              </div>
              <!-- Kanal -->
              <div
                v-else-if="row.kind === 'channel'"
                :key="row.key"
                role="treeitem"
                tabindex="0"
                class="row-item channel-row"
                :class="{ selected: isSelected('channel', row.channel.cid) }"
                :style="indent(row)"
                @click="selectChannel(row.channel)"
                @keydown.enter="selectChannel(row.channel)"
              >
                <v-icon small class="mr-2">{{
                  flag(row.channel.channelFlagPassword)
                    ? "mdi-lock-outline"
                    : "mdi-pound"
                }}</v-icon>
                <span class="text-truncate font-weight-medium">{{
                  row.channel.channelName
                }}</span>
                <v-icon
                  v-if="flag(row.channel.channelFlagDefault)"
                  x-small
                  class="ml-1"
                  title="Standardkanal: hier landet, wer neu kommt"
                  >mdi-home</v-icon
                >
                <v-spacer></v-spacer>
                <span v-if="row.people" class="text-caption text--secondary ml-2">{{
                  row.people
                }}</span>
              </div>
              <!-- Person -->
              <div
                v-else
                :key="row.key"
                role="treeitem"
                tabindex="0"
                class="row-item person-row"
                :class="{ selected: isSelected('person', row.person.clid) }"
                :style="indent(row)"
                @click="selectPerson(row.person)"
                @keydown.enter="selectPerson(row.person)"
              >
                <v-icon
                  small
                  class="mr-2"
                  :color="flag(row.person.clientFlagTalking) ? 'success' : undefined"
                  :title="statusText(row.person)"
                  >{{ statusIcon(row.person) }}</v-icon
                >
                <span class="text-truncate">{{ row.person.clientNickname }}</span>
                <v-icon
                  v-if="isLocked(row.person)"
                  x-small
                  color="warning"
                  class="ml-1"
                  >mdi-lock</v-icon
                >
                <v-spacer></v-spacer>
                <span class="text-caption text--secondary ml-2 text-no-wrap">{{
                  badge(row.person)
                }}</span>
              </div>
            </template>
            <p
              v-if="!loading && !rows.length"
              class="text-body-2 text--secondary pa-4 mb-0"
            >
              {{ search ? "Niemand mit diesem Namen online." : "Keine Kanäle." }}
            </p>
          </div>
        </v-card>
      </v-col>

      <!-- Detail rechts (Desktop) -->
      <v-col v-if="!mobile" cols="12" md="6" lg="7">
        <div class="panel-sticky">
          <component
            :is="panelComponent"
            v-if="panelComponent"
            v-bind="panelProps"
            @changed="load"
            @close="clearSelection"
            @select-person="selectPersonByClid"
            @select-channel="selectChannelByCid"
            @new-sub="newSubChannel"
          ></component>
          <v-card v-else outlined class="pa-6 text-body-2 text--secondary">
            Links einen Kanal oder eine Person antippen. Hier kannst du dann
            Kanäle ändern, Rollen im Kanal vergeben, Leute verschieben,
            anschreiben, rauswerfen oder bannen.
          </v-card>
        </div>
      </v-col>
    </v-row>

    <!-- Handy: Detail als Vollbild -->
    <v-dialog
      v-if="mobile"
      :value="Boolean(panelComponent)"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
      @input="(v) => !v && clearSelection()"
    >
      <v-card tile>
        <v-toolbar dense flat>
          <v-btn icon aria-label="Zurück" @click="clearSelection"
            ><v-icon>mdi-arrow-left</v-icon></v-btn
          >
          <v-toolbar-title class="text-subtitle-1">Zurück</v-toolbar-title>
        </v-toolbar>
        <div class="pa-2">
          <component
            :is="panelComponent"
            v-if="panelComponent"
            v-bind="panelProps"
            @changed="load"
            @close="clearSelection"
            @select-person="selectPersonByClid"
            @select-channel="selectChannelByCid"
            @new-sub="newSubChannel"
          ></component>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  loadConfig,
  ensureServer,
  asList,
  resolveGroups,
  channelRoleLabel,
  errorText,
} from "@/iconic/rechte";
import { buildRows } from "@/iconic/live";

const flag = (v) => v === true || v === 1 || v === "1";

export default {
  components: {
    LivePerson: () => import("@/components/LivePerson"),
    LiveChannel: () => import("@/components/LiveChannel"),
  },
  data() {
    return {
      loading: true,
      search: "",
      showEmpty: true,
      channels: [],
      clients: [],
      groups: { serverRoles: [], channelRoles: [] },
      config: { protectedDbIds: [] },
      serverInfo: {},
      selected: null, // { type: 'channel'|'person'|'new', id }
      timer: null,
      reloadTimer: null,
    };
  },
  computed: {
    mobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    rows() {
      return buildRows(this.channels, this.clients, {
        showEmpty: this.showEmpty,
        search: this.search,
      });
    },
    onlineCount() {
      return this.clients.length;
    },
    selectedChannel() {
      if (!this.selected || this.selected.type !== "channel") return null;
      return this.channels.find((c) => String(c.cid) === this.selected.id) || null;
    },
    selectedPerson() {
      if (!this.selected || this.selected.type !== "person") return null;
      return this.clients.find((c) => String(c.clid) === this.selected.id) || null;
    },
    panelComponent() {
      if (!this.selected) return null;
      if (this.selected.type === "person") return "LivePerson";
      return "LiveChannel";
    },
    panelProps() {
      const base = {
        groups: this.groups,
        channels: this.channels,
        clients: this.clients,
        config: this.config,
        serverInfo: this.serverInfo,
      };
      if (this.selected && this.selected.type === "person")
        return { ...base, person: this.selectedPerson, clid: this.selected.id };
      if (this.selected && this.selected.type === "new")
        return { ...base, channel: null, parentCid: this.selected.id };
      return { ...base, channel: this.selectedChannel, parentCid: null };
    },
  },
  methods: {
    flag,
    indent(row) {
      const sel =
        row.kind === "channel"
          ? this.isSelected("channel", row.channel.cid)
          : this.isSelected("person", row.person.clid);
      return {
        paddingLeft: `${12 + row.depth * 18}px`,
        borderLeftColor: sel ? this.$vuetify.theme.currentTheme.primary : "transparent",
      };
    },
    isSelected(type, id) {
      return this.selected && this.selected.type === type && this.selected.id === String(id);
    },
    isLocked(p) {
      return this.config.protectedDbIds.includes(String(p.clientDatabaseId));
    },
    statusIcon(p) {
      if (flag(p.clientAway)) return "mdi-sleep";
      if (flag(p.clientOutputMuted)) return "mdi-headphones-off";
      if (flag(p.clientInputMuted)) return "mdi-microphone-off";
      return "mdi-account";
    },
    statusText(p) {
      if (flag(p.clientAway)) return "abwesend";
      if (flag(p.clientOutputMuted)) return "hört nichts";
      if (flag(p.clientInputMuted)) return "Mikrofon aus";
      return "online";
    },
    badge(p) {
      const sg = (p.clientServergroups || []).map(String);
      const admin = this.groups.serverRoles.find((r) => r.key === "admin");
      if (admin && sg.includes(admin.sgid)) return "Server Admin";
      if (String(p.clientChannelGroupId) !== this.groups.defaultCg)
        return channelRoleLabel(this.groups, p.clientChannelGroupId);
      return "";
    },
    selectChannel(channel) {
      this.selected = { type: "channel", id: String(channel.cid) };
    },
    selectPerson(person) {
      this.selected = { type: "person", id: String(person.clid) };
    },
    selectPersonByClid(clid) {
      this.selected = { type: "person", id: String(clid) };
    },
    selectChannelByCid(cid) {
      this.selected = { type: "channel", id: String(cid) };
    },
    newChannel() {
      this.selected = { type: "new", id: "0" };
    },
    newSubChannel(cid) {
      this.selected = { type: "new", id: String(cid) };
    },
    clearSelection() {
      this.selected = null;
    },
    async load() {
      try {
        await ensureServer();
        const ts = this.$TeamSpeak;
        const [config, serverInfo, serverGroups, channelGroups, channels, clients] =
          await Promise.all([
            loadConfig(),
            ts.execute("serverinfo").then((r) => asList(r)[0]),
            ts.execute("servergrouplist").then(asList),
            ts.execute("channelgrouplist").then(asList),
            ts.execute("channellist", {}, ["-topic", "-flags", "-voice", "-limits"]).then(asList),
            ts.execute("clientlist", {}, ["-groups", "-voice", "-away", "-times"]).then(asList),
          ]);
        this.config = config;
        this.serverInfo = serverInfo;
        this.groups = resolveGroups(serverInfo, serverGroups, channelGroups);
        this.channels = channels;
        this.clients = clients.filter((c) => String(c.clientType) === "0");
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.loading = false;
    },
    // Ereignisse von TeamSpeak kommen oft gebündelt: kurz sammeln, dann einmal neu laden
    scheduleReload() {
      clearTimeout(this.reloadTimer);
      this.reloadTimer = setTimeout(() => this.load(), 400);
    },
  },
  created() {
    this.load();
    this.events = [
      "clientmoved",
      "clientconnect",
      "clientdisconnect",
      "channeledit",
      "channelcreate",
      "channelmoved",
      "channeldelete",
    ];
    this.events.forEach((e) => this.$TeamSpeak.on(e, this.scheduleReload));
    // Sicherheitsnetz, falls ein Ereignis fehlt
    this.timer = setInterval(() => !document.hidden && this.load(), 20000);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearTimeout(this.reloadTimer);
    this.events.forEach((e) =>
      this.$TeamSpeak.__proto__.removeEventListener(e, this.scheduleReload)
    );
  },
};
</script>

<style scoped>
.search {
  max-width: 260px;
}
.tree {
  max-height: calc(100vh - 230px);
  overflow-y: auto;
  padding: 4px 0 8px;
}
.row-item {
  display: flex;
  align-items: center;
  min-height: 36px;
  padding-right: 12px;
  cursor: pointer;
  border-left: 3px solid transparent;
}
.row-item:hover {
  background: rgba(128, 128, 128, 0.12);
}
.row-item:focus-visible {
  outline: 2px solid currentColor;
  outline-offset: -2px;
}
.row-item.selected {
  background: rgba(128, 128, 128, 0.2);
}
.person-row {
  min-height: 32px;
}
.spacer-row {
  padding: 6px 12px;
  cursor: pointer;
}
.panel-sticky {
  position: sticky;
  top: 76px;
}
@media (max-width: 959px) {
  .tree {
    max-height: none;
  }
}
</style>
